import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NoteCategory {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  isScheduling: boolean;
  allowedRoles: string[];
  displayOrder: number;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoteCategoryPayload {
  name: string;
  description?: string | null;
  color?: string | null;
  is_active?: boolean;
  is_scheduling?: boolean;
  allowed_roles?: string[];
  display_order?: number;
}

export interface UpdateNoteCategoryPayload {
  name?: string;
  description?: string | null;
  color?: string | null;
  is_active?: boolean;
  is_scheduling?: boolean;
  allowed_roles?: string[];
  display_order?: number;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const noteCategoriesKeys = {
  all: ['note-categories'] as const,
  list: () => [...noteCategoriesKeys.all, 'list'] as const,
  detail: (id: string) => [...noteCategoriesKeys.all, 'detail', id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all note categories available to the current user.
 * Results are already filtered by the user's role on the backend.
 */
export function useNoteCategories(options?: { includeInactive?: boolean }) {
  return useQuery({
    queryKey: noteCategoriesKeys.list(),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (options?.includeInactive) {
        params.include_inactive = 'true';
      }

      const response = await apiClient.get<ApiResponse<Array<Record<string, unknown>>>>(
        '/contacts/note-categories',
        { params },
      );

      const raw = response.data.data ?? [];
      return raw.map(mapNoteCategory);
    },
  });
}

/**
 * Fetch a single note category by ID.
 */
export function useNoteCategory(categoryId: string) {
  return useQuery({
    queryKey: noteCategoriesKeys.detail(categoryId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/contacts/note-categories/${categoryId}`,
      );
      return mapNoteCategory(response.data.data);
    },
    enabled: !!categoryId,
  });
}

/**
 * Create a new note category.
 */
export function useCreateNoteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateNoteCategoryPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/contacts/note-categories',
        payload,
      );
      return mapNoteCategory(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteCategoriesKeys.list() });
    },
  });
}

/**
 * Update an existing note category.
 */
export function useUpdateNoteCategory(categoryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateNoteCategoryPayload) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/contacts/note-categories/${categoryId}`,
        payload,
      );
      return mapNoteCategory(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteCategoriesKeys.list() });
      queryClient.invalidateQueries({ queryKey: noteCategoriesKeys.detail(categoryId) });
    },
  });
}

/**
 * Delete a note category.
 */
export function useDeleteNoteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      await apiClient.delete(`/contacts/note-categories/${categoryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteCategoriesKeys.list() });
    },
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapNoteCategory(raw: Record<string, unknown>): NoteCategory {
  const allowedRoles = raw.allowed_roles ?? raw.allowedRoles;

  return {
    id: raw.id as string,
    name: (raw.name as string) ?? '',
    description: (raw.description as string) ?? null,
    color: (raw.color as string) ?? null,
    isActive: (raw.is_active as boolean) ?? (raw.isActive as boolean) ?? true,
    isScheduling: (raw.is_scheduling as boolean) ?? (raw.isScheduling as boolean) ?? false,
    allowedRoles: Array.isArray(allowedRoles)
      ? allowedRoles
      : typeof allowedRoles === 'string'
        ? JSON.parse(allowedRoles)
        : [],
    displayOrder: (raw.display_order as number) ?? (raw.displayOrder as number) ?? 0,
    createdById: (raw.created_by_id as string) ?? (raw.createdById as string) ?? null,
    createdAt: (raw.created_at as string) ?? (raw.createdAt as string) ?? '',
    updatedAt: (raw.updated_at as string) ?? (raw.updatedAt as string) ?? '',
  };
}
